import { planHasCapability } from "@workspace/commerce/billing/capabilities";
import { toInternalCurrencyCode } from "@workspace/shared/currency";

export type OrganizationFiscalSnapshot = {
  taxId?: string | null;
  legalName?: string | null;
  taxCategory?: string | null;
  personType?: string | null;
  address?: string | null;
  provinceId?: string | null;
  city?: string | null;
  activitiesStartedAt?: string | null;
};

export type OrganizationPlanType =
  | "starter"
  | "pro"
  | "max"
  | "starter_v2"
  | "pro_v2"
  | "max_v2"
  | "enterprise"
  | null
  | undefined;

export type OrganizationCurrency = "PES" | "DOL";

type OrganizationSettings = Record<string, unknown> | null | undefined;

const DEFAULT_ENABLED_CURRENCIES: OrganizationCurrency[] = ["PES"];

function isOrganizationCurrency(value: unknown): value is OrganizationCurrency {
  return value === "PES" || value === "DOL";
}

function readEnabledCurrencies(settings: unknown): unknown {
  if (!(settings && typeof settings === "object" && !Array.isArray(settings))) {
    return;
  }

  return (settings as { enabledCurrencies?: unknown }).enabledCurrencies;
}

/**
 * Returns `true` when the organization has enough fiscal metadata to
 * proceed with tasks that require ARCA onboarding to be complete.
 */
export function hasOrganizationFiscalData(
  organization: OrganizationFiscalSnapshot | null | undefined
): boolean {
  if (!organization) {
    return false;
  }

  return Boolean(
    organization.taxId &&
    organization.legalName &&
    organization.taxCategory &&
    organization.personType &&
    organization.address &&
    organization.provinceId &&
    organization.activitiesStartedAt
  );
}

export function hasFullPlanCapabilities(
  planType: OrganizationPlanType
): boolean {
  return (
    planType === "max" ||
    planType === "starter_v2" ||
    planType === "pro_v2" ||
    planType === "max_v2" ||
    planType === "enterprise"
  );
}

function readAccountingEnabledSetting(settings: OrganizationSettings): boolean {
  if (typeof settings?.accountingEnabled === "boolean") {
    return settings.accountingEnabled;
  }

  if (typeof settings?.accountsModuleEnabled === "boolean") {
    return settings.accountsModuleEnabled;
  }

  return false;
}

export function getEffectiveAccountingEnabled(
  planType: OrganizationPlanType,
  settings: OrganizationSettings
): boolean {
  return (
    hasFullPlanCapabilities(planType) && readAccountingEnabledSetting(settings)
  );
}

export function getEffectivePromotionalPriceEnabled(
  settings: OrganizationSettings
): boolean {
  return (settings?.promotionalPriceEnabled as boolean | undefined) === true;
}

export function getEffectiveImportDespachosEnabled(
  planType: OrganizationPlanType,
  settings: OrganizationSettings
): boolean {
  return (
    hasFullPlanCapabilities(planType) &&
    ((settings?.importDespachosEnabled as boolean | undefined) ?? false)
  );
}

export function getEffectiveEnabledCurrencies(
  planType: OrganizationPlanType,
  settings: unknown
): OrganizationCurrency[] {
  if (!planHasCapability(planType, "multicurrency", settings)) {
    return DEFAULT_ENABLED_CURRENCIES;
  }

  const enabledCurrencies = readEnabledCurrencies(settings);
  const rawCurrencies = Array.isArray(enabledCurrencies)
    ? enabledCurrencies.filter(isOrganizationCurrency)
    : [];

  if (rawCurrencies.length === 0) {
    return DEFAULT_ENABLED_CURRENCIES;
  }

  return Array.from(new Set(rawCurrencies));
}

export function hasEffectiveMulticurrencyCapability(
  planType: OrganizationPlanType,
  settings: unknown
): boolean {
  return planHasCapability(planType, "multicurrency", settings);
}

export function isEffectiveCurrencyEnabled(
  planType: OrganizationPlanType,
  settings: OrganizationSettings,
  currency: unknown
): currency is OrganizationCurrency {
  return (
    isOrganizationCurrency(currency) &&
    getEffectiveEnabledCurrencies(planType, settings).includes(currency)
  );
}

export function resolveEffectiveCurrency(
  planType: OrganizationPlanType,
  settings: unknown,
  requestedCurrency: unknown
): OrganizationCurrency {
  const enabledCurrencies = getEffectiveEnabledCurrencies(planType, settings);
  const normalizedCurrency = toInternalCurrencyCode(requestedCurrency);

  return normalizedCurrency && enabledCurrencies.includes(normalizedCurrency)
    ? normalizedCurrency
    : (enabledCurrencies[0] ?? "PES");
}
